import { useEffect, useState } from "react";
import LibraryPicker from "./LibraryPicker.js";
import { importFromLibrary, type ImportDestination } from "../api.js";

/**
 * Picking questions from the library into the thing you are already building.
 *
 * Over a modal rather than a trip to the Library tab: the teacher is halfway
 * through a sheet, and a page navigation means losing sight of the questions
 * already on it and finding the way back afterwards. What lands here lands
 * inside the sheet or on the paper, never as a loose assignment.
 */
export default function LibraryImportModal({
  into,
  title,
  importLabel,
  onClose,
  onImported,
}: {
  into: ImportDestination;
  /** What is being added to, e.g. "Lab Sheet 1". */
  title: string;
  importLabel: string;
  onClose: () => void;
  /** Fired after every successful import, so the caller can refresh. */
  onImported: (summary: string) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  // Escape closes it. A modal that can only be dismissed by finding the button
  // is one people leave open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleImport(ids: string[]): Promise<string[]> {
    setError(null);
    setStatus(null);
    try {
      const r = await importFromLibrary(into, ids);
      const cases = r.imported.reduce((n, q) => n + q.testCasesCopied, 0);
      const summary =
        r.imported.length === 1
          ? `Added "${r.imported[0].title}" (${cases} test cases).`
          : `Added ${r.imported.length} questions (${cases} test cases).`;
      setStatus(summary + (r.skipped.length ? ` ${r.skipped.length} skipped: ${r.skipped[0].reason}` : ""));
      onImported(summary);
      return r.imported.map((q) => q.sourceId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed.");
      // Nothing landed, so nothing leaves the selection: the teacher can fix
      // the problem and press the same button again.
      return [];
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Add questions from the library to ${title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="between modal-head">
          <div>
            <strong>Add from the question library</strong>
            <div className="muted" style={{ fontSize: 13 }}>
              Into {title}. Each import is an independent copy — editing it will not change the
              original.
            </div>
          </div>
          <button onClick={onClose}>Done</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-banner">{error}</div>}
          {status && <div className="notice">{status}</div>}
          <LibraryPicker importLabel={importLabel} onImport={handleImport} onError={setError} />
        </div>
      </div>
    </div>
  );
}
